/**
 * kpiModel.js — Modèle d'affichage KPI & Reporting (pur, sans React).
 *
 * Centralise : plateformes, catalogue d'indicateurs par plateforme, adaptateur de
 * rétro-compatibilité des documents de détail, agrégat national (France), résolution
 * des cibles depuis les objectifs, helpers de période et de formatage.
 *
 * Source des données : cron Metricool (server/crons.js) → ep:reporting (résumé/mois/club)
 * et ep:reporting-details:<mois>:<clubId>.platforms[<plateforme>].
 */

export const NATIONAL_CLUB_ID = "1774259149160699444"; // « France » : synthèse, jamais synchronisée

// Entités de reporting hors clubs padel (restaurants, compte national), synchronisées
// par le cron (ep:reporting avec ces id). Affichées dans le KPI, groupées, hors classement.
export const REPORTING_ENTITIES = [
  { id: "ent-4483914", name: "Faim de Piste – Saint Priest", color: "#C0392B", family: "restaurant" },
  { id: "ent-5482242", name: "Faim de Piste – La Boisse", color: "#E67E22", family: "restaurant" },
  { id: "ent-4936958", name: "Esprit Padel (national)", color: "#8E44AD", family: "national" },
];
export const ENTITY_FAMILIES = [
  { key: "restaurant", label: "Restaurants" },
  { key: "national", label: "National" },
];

/* ── Plateformes ──────────────────────────────────────────────────────── */
export const PLATFORMS = [
  { id: "instagram", label: "Instagram", short: "IG", icon: "📸", kind: "social" },
  { id: "facebook", label: "Facebook", short: "FB", icon: "👥", kind: "social" },
  { id: "gmb", label: "Google Business", short: "GBP", icon: "🔍", kind: "local" },
  { id: "web", label: "Site web", short: "Web", icon: "🌐", kind: "web" },
];
export const PLATFORM_BY_ID = Object.fromEntries(PLATFORMS.map((p) => [p.id, p]));

/** Libellés de canaux de campagne (CampagnesV2) → identifiant plateforme KPI. */
export const CHANNEL_TO_PLATFORM = {
  Instagram: "instagram",
  Facebook: "facebook",
  "Google Business": "gmb",
  "Site web": "web",
  TikTok: null,
  LinkedIn: null,
};

/* ── Catalogue d'indicateurs ──────────────────────────────────────────── */
// fmt : "int" | "num" | "pct" | "points"
export const INDICATORS = {
  abonnes: { key: "abonnes", label: "Abonnés", unit: "abonnés", fmt: "int" },
  evolutionAbonnes: { key: "evolutionAbonnes", label: "Évolution abonnés", unit: "abonnés", fmt: "int", signed: true },
  txEvol: { key: "txEvol", label: "Taux d'évolution abonnés", unit: "%", fmt: "pct" },
  publications: { key: "publications", label: "Publications", unit: "posts", fmt: "int" },
  reels: { key: "reels", label: "Reels", unit: "reels", fmt: "int" },
  stories: { key: "stories", label: "Stories", unit: "stories", fmt: "int" },
  porteeMoy: { key: "porteeMoy", label: "Portée moy. / post", unit: "pers.", fmt: "int" },
  porteeTotale: { key: "porteeTotale", label: "Portée totale", unit: "pers.", fmt: "int" },
  vues: { key: "vues", label: "Vues vidéos / reels", unit: "vues", fmt: "int" },
  interactions: { key: "interactions", label: "Interactions", unit: "", fmt: "int" },
  jaimes: { key: "jaimes", label: "J'aime", unit: "", fmt: "int" },
  commentaires: { key: "commentaires", label: "Commentaires", unit: "", fmt: "int" },
  partages: { key: "partages", label: "Partages", unit: "", fmt: "int" },
  enregistrements: { key: "enregistrements", label: "Enregistrements", unit: "", fmt: "int" },
  engagement: { key: "engagement", label: "Taux d'engagement", unit: "%", fmt: "pct", help: "Interactions ÷ portée × 100" },
  clics: { key: "clics", label: "Clics vers le site / liens", unit: "clics", fmt: "int" },
  // ── Google Business (modèle local) ──
  impressionsTotal: { key: "impressionsTotal", label: "Impressions (maps + search)", unit: "", fmt: "int" },
  impressionsMaps: { key: "impressionsMaps", label: "Impressions Maps", unit: "", fmt: "int" },
  impressionsSearch: { key: "impressionsSearch", label: "Impressions Search", unit: "", fmt: "int" },
  callClicks: { key: "callClicks", label: "Appels", unit: "", fmt: "int" },
  websiteClicks: { key: "websiteClicks", label: "Clics site web", unit: "", fmt: "int" },
  directionRequests: { key: "directionRequests", label: "Demandes d'itinéraire", unit: "", fmt: "int" },
  bookings: { key: "bookings", label: "Réservations", unit: "", fmt: "int" },
  actionsTotal: { key: "actionsTotal", label: "Actions totales", unit: "", fmt: "int" },
  reviews: { key: "reviews", label: "Avis reçus", unit: "avis", fmt: "int" },
  avgRating: { key: "avgRating", label: "Note moyenne", unit: "/5", fmt: "num" },
  // ── Site web (Google Analytics 4) ──
  utilisateurs: { key: "utilisateurs", label: "Utilisateurs", unit: "", fmt: "int" },
  nouveauxUtilisateurs: { key: "nouveauxUtilisateurs", label: "Nouveaux utilisateurs", unit: "", fmt: "int" },
  sessions: { key: "sessions", label: "Sessions", unit: "", fmt: "int" },
  sessionsEngagees: { key: "sessionsEngagees", label: "Sessions engagées", unit: "", fmt: "int" },
  pagesVues: { key: "pagesVues", label: "Pages vues", unit: "", fmt: "int" },
  tauxEngagement: { key: "tauxEngagement", label: "Taux d'engagement", unit: "%", fmt: "pct", help: "Part des sessions engagées" },
  tauxRebond: { key: "tauxRebond", label: "Taux de rebond", unit: "%", fmt: "pct" },
  dureeMoySession: { key: "dureeMoySession", label: "Durée moy. session (s)", unit: "s", fmt: "num" },
  pagesParSession: { key: "pagesParSession", label: "Pages / session", unit: "", fmt: "num" },
  conversions: { key: "conversions", label: "Conversions", unit: "", fmt: "int" },
};

/** Indicateurs affichés au tableau de bord, par plateforme. */
export const PLATFORM_INDICATORS = {
  instagram: [
    "abonnes", "evolutionAbonnes", "publications", "reels", "stories",
    "porteeMoy", "porteeTotale", "vues", "interactions", "engagement",
    "jaimes", "commentaires", "partages", "enregistrements",
  ],
  facebook: [
    "abonnes", "evolutionAbonnes", "publications", "porteeMoy", "porteeTotale",
    "vues", "interactions", "engagement", "jaimes", "commentaires", "partages",
  ],
  gmb: [
    "impressionsTotal", "impressionsMaps", "impressionsSearch",
    "callClicks", "websiteClicks", "directionRequests", "bookings",
    "actionsTotal", "reviews", "avgRating",
  ],
  web: [
    "utilisateurs", "nouveauxUtilisateurs", "sessions", "sessionsEngagees",
    "pagesVues", "tauxEngagement", "tauxRebond", "dureeMoySession", "pagesParSession",
  ], // Google Analytics 4
};

/** Indicateurs de progression proposés au classement, par plateforme. */
export const RANKING_INDICATORS = {
  instagram: ["abonnes", "porteeTotale", "vues", "interactions", "engagement"],
  facebook: ["abonnes", "porteeTotale", "interactions", "engagement"],
  gmb: ["impressionsTotal", "actionsTotal", "websiteClicks"],
  web: ["utilisateurs", "sessions", "pagesVues"],
};

/* ── Rétro-compatibilité des documents de détail ──────────────────────── */
/**
 * Normalise un doc ep:reporting-details vers la forme { <plateforme>: {...} }.
 * Les documents antérieurs au multi-réseaux n'ont pas de champ `platforms` :
 * leur contenu racine (= Instagram) est reconstruit sous platforms.instagram.
 */
export function detailsToPlatforms(detail) {
  if (!detail) return {};
  if (detail.platforms && typeof detail.platforms === "object") return detail.platforms;
  return {
    instagram: {
      available: true,
      summary: detail.summary || {},
      posts: detail.posts || [],
      reels: detail.reels || [],
      stories: detail.stories || [],
      followersTimeline: detail.followersTimeline || [],
      demographics: detail.demographics || {},
      hashtags: detail.hashtags || [],
    },
  };
}

/** Résumé d'une plateforme depuis un doc de détail (ou null si indisponible). */
export function platformSummary(detail, platform) {
  const p = detailsToPlatforms(detail)[platform];
  if (!p || p.available === false) return null;
  return p.summary || null;
}

/** Posts/reels/stories d'une plateforme, aplatis avec un champ `format`. */
export function platformPublications(detail, platform) {
  const p = detailsToPlatforms(detail)[platform];
  if (!p || p.available === false) return [];
  const tag = (arr, format) => (arr || []).map((x) => ({ ...x, format }));
  return [
    ...tag(p.posts, "post"),
    ...tag(p.reels, "reel"),
    ...tag(p.stories, "story"),
  ];
}

/* ── Agrégat national (France) ────────────────────────────────────────── */
const SUMMABLE = [
  "evolutionAbonnes", "abonnes", "publications", "reels", "stories",
  "porteeTotale", "vues", "interactions", "jaimes", "commentaires",
  "partages", "enregistrements", "storiesImpressions",
  "impressionsTotal", "impressionsMaps", "impressionsSearch",
  "callClicks", "websiteClicks", "directionRequests", "bookings",
  "actionsTotal", "reviews",
];
/**
 * Synthèse France = somme des clubs. L'engagement n'est PAS moyenné : il est
 * recalculé Σinteractions / Σportée × 100. La portée moyenne est recalculée
 * sur le total de publications.
 */
export function nationalAggregate(summaries) {
  const rows = (summaries || []).filter(Boolean);
  if (!rows.length) return null;
  const isWeb = rows.some((r) => r.source === "ga4" || r.utilisateurs !== undefined || r.sessions !== undefined);
  // Somme de tous les champs numériques présents (gère réseaux, GMB et Web).
  const agg = {};
  const numKeys = new Set();
  rows.forEach((r) => Object.keys(r).forEach((k) => { if (typeof r[k] === "number") numKeys.add(k); }));
  numKeys.forEach((k) => { agg[k] = rows.reduce((t, r) => t + Number(r[k] || 0), 0); });
  if (isWeb) {
    const sessions = agg.sessions || 0;
    agg.tauxEngagement = sessions > 0 ? Number((((agg.sessionsEngagees || 0) / sessions) * 100).toFixed(2)) : 0;
    agg.tauxRebond = sessions > 0 ? Number((100 - agg.tauxEngagement).toFixed(2)) : 0;
    agg.pagesParSession = sessions > 0 ? Number(((agg.pagesVues || 0) / sessions).toFixed(2)) : 0;
    const totDur = rows.reduce((t, r) => t + Number(r.dureeMoySession || 0) * Number(r.sessions || 0), 0);
    agg.dureeMoySession = sessions > 0 ? Math.round(totDur / sessions) : 0;
    agg.source = "ga4"; agg.__national = true;
    return agg;
  }
  agg.porteeMoy = agg.publications > 0 ? Number(((agg.porteeTotale || 0) / agg.publications).toFixed(0)) : 0;
  agg.engagement = agg.porteeTotale > 0 ? Number((((agg.interactions || 0) / agg.porteeTotale) * 100).toFixed(2)) : 0;
  const firstFollowers = (agg.abonnes || 0) - (agg.evolutionAbonnes || 0);
  agg.txEvol = firstFollowers > 0 ? Number((((agg.evolutionAbonnes || 0) / firstFollowers) * 100).toFixed(2)) : 0;
  agg.__national = true;
  return agg;
}

/* ── Objectifs → cibles ───────────────────────────────────────────────── */
/**
 * Trouve la cible (objectif) correspondant à un indicateur/club pour le mois donné.
 * Mappe quelques clés d'indicateur KPI vers les metricKey d'objectifs.
 */
const INDICATOR_TO_METRICKEY = {
  abonnes: ["abonnes"],
  evolutionAbonnes: ["nouveauxAbonnes"],
  engagement: ["engagement"],
  porteeMoy: ["porteeMoy", "porteeMoyJour"],
  interactions: ["interactions"],
  publications: ["publications", "postsTotal"],
  reels: ["reels"],
  stories: ["stories"],
  vues: ["vues"],
};
export function resolveTarget(objectives, clubId, indicator, monthKey) {
  const keys = INDICATOR_TO_METRICKEY[indicator];
  if (!keys || !Array.isArray(objectives)) return null;
  const inMonth = (o) => {
    if (!monthKey || !o.metricPeriodFrom) return true;
    return String(o.metricPeriodFrom).slice(0, 7) === monthKey || String(o.metricPeriodTo || "").slice(0, 7) === monthKey;
  };
  const match = objectives
    .filter((o) => String(o.club) === String(clubId) && keys.includes(o.metricKey) && inMonth(o))
    .sort((a, b) => String(b.metricPeriodTo || "").localeCompare(String(a.metricPeriodTo || "")))[0];
  if (!match) return null;
  return {
    target: Number(match.target || 0),
    current: match.current != null ? Number(match.current) : null,
    unit: match.unit || INDICATORS[indicator]?.unit || "",
    progress: match.progress != null ? Number(match.progress) : null,
    title: match.title || "",
  };
}

/* ── Périodes ─────────────────────────────────────────────────────────── */
export const MONTH_NAMES = [
  "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
  "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
];
export const formatMonth = (m) => {
  if (!m) return "—";
  const [y, mo] = String(m).split("-");
  return `${MONTH_NAMES[parseInt(mo, 10) - 1] || mo} ${y}`;
};
export const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
export const prevMonthKey = (m) => {
  const [y, mo] = String(m).split("-").map(Number);
  const d = new Date(y, mo - 2, 1);
  return monthKey(d);
};
/** Tous les mois d'une année sous forme "YYYY-MM". */
export const monthsOfYear = (year) =>
  Array.from({ length: 12 }, (_, i) => `${year}-${String(i + 1).padStart(2, "0")}`);
/** Liste des mois disponibles dans ep:reporting (triés). */
export const availableMonths = (reporting) =>
  Object.keys(reporting || {})
    .filter((k) => Array.isArray(reporting[k]) && reporting[k].length)
    .sort();

/* ── Formatage ────────────────────────────────────────────────────────── */
export const nf = new Intl.NumberFormat("fr-FR");
export function fmtValue(value, fmt = "int", opts = {}) {
  if (value == null || value === "" || (typeof value === "number" && isNaN(value))) return null;
  const n = Number(value);
  const sign = opts.signed && n > 0 ? "+" : "";
  switch (fmt) {
    case "pct":
      return `${sign}${nf.format(Number(n.toFixed(2)))} %`;
    case "points":
      return `${sign}${nf.format(Number(n.toFixed(2)))} pts`;
    case "num":
      return `${sign}${nf.format(Number(n.toFixed(2)))}`;
    case "int":
    default:
      return `${sign}${nf.format(Math.round(n))}`;
  }
}
/** Variation relative (%) entre deux valeurs ; null si base nulle. */
export function deltaPct(current, previous) {
  if (previous == null || current == null) return null;
  if (Math.abs(previous) < 1e-9) return null;
  return Number((((current - previous) / previous) * 100).toFixed(1));
}

export const ENGAGEMENT_DEFINITION = "Taux d'engagement = interactions ÷ portée × 100 (sur la période).";
